import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import BoltIcon from '@mui/icons-material/Bolt';
import BarChartIcon from '@mui/icons-material/BarChart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DS } from '../theme';

interface LandingPageProps {
  onStart: () => void;
  onGuestAccess: () => void;
}

/* ── Pricing Coming Soon page ── */
const PricingPage: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <Box sx={{ minHeight: '100vh', backgroundColor: DS.bg, display: 'flex', flexDirection: 'column' }}>
    <Box sx={{
      height: 64, display: 'flex', alignItems: 'center', px: { xs: 3, md: 6 },
      borderBottom: `1px solid ${DS.border}`, backgroundColor: DS.bg2,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 28, height: 28, backgroundColor: DS.accent, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>DS</Typography>
        </Box>
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: DS.text, letterSpacing: '-0.3px' }}>DataScope</Typography>
      </Box>
    </Box>
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: { xs: 3, md: 6 }, pb: 12 }}>
      <Typography sx={{ fontSize: { xs: '2.5rem', md: '4rem' }, fontWeight: 700, color: DS.text, letterSpacing: '-0.04em', lineHeight: 1.1, mb: 2 }}>
        Coming Soon
      </Typography>
      <Typography sx={{ fontSize: '1.0625rem', color: DS.textMuted, maxWidth: 420, mx: 'auto', mb: 5, lineHeight: 1.6 }}>
        We're working on flexible plans for individuals and teams. Check back shortly.
      </Typography>
      <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack}
        sx={{ px: 3.5, py: 1.25, borderRadius: '9999px', fontSize: '0.9375rem' }}>
        Back to Home
      </Button>
    </Box>
  </Box>
);

/* ── Main Landing Page ── */
const LandingPage: React.FC<LandingPageProps> = ({ onStart, onGuestAccess }) => {
  const [showPricing, setShowPricing] = useState(false);

  if (showPricing) return <PricingPage onBack={() => setShowPricing(false)} />;

  const scrollToAbout = () => document.getElementById('about-section')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: DS.bg, fontFamily: '"DM Sans", "Inter", sans-serif' }}>

      {/* ── Nav ── */}
      <Box sx={{
        height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: { xs: 3, md: 6 }, backgroundColor: DS.bg2, borderBottom: `1px solid ${DS.border}`,
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 28, height: 28, backgroundColor: DS.accent, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>DS</Typography>
          </Box>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: DS.text, letterSpacing: '-0.3px' }}>DataScope</Typography>
        </Box>

        {/* Nav links */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 3, md: 4 } }}>
          {[
            { label: 'About', action: scrollToAbout },
            { label: 'Pricing', action: () => setShowPricing(true) },
          ].map(item => (
            <Typography key={item.label} onClick={item.action}
              sx={{ fontSize: '0.9375rem', color: DS.textMuted, cursor: 'pointer', transition: 'color 0.15s', '&:hover': { color: DS.text } }}>
              {item.label}
            </Typography>
          ))}
          <Button variant="outlined" size="small" onClick={onStart}
            sx={{ borderRadius: '9999px', px: 2.5, py: 0.75, fontSize: '0.875rem', borderColor: DS.border, color: DS.textMuted, '&:hover': { borderColor: DS.borderStrong, color: DS.text } }}>
            Sign In
          </Button>
        </Box>
      </Box>

      {/* ── Hero ── */}
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
        pt: { xs: 10, md: 16 }, pb: { xs: 10, md: 14 }, px: { xs: 3, md: 6 },
      }}>
        {/* Badge */}
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 1,
          px: '12px', py: '6px', borderRadius: '9999px',
          backgroundColor: DS.accentDim, border: `1px solid rgba(107,94,248,0.25)`,
          mb: 4,
        }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: DS.accent }} />
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: DS.accent, letterSpacing: '0.04em' }}>
            No data science degree required
          </Typography>
        </Box>

        {/* Headline */}
        <Typography sx={{
          fontSize: { xs: '3.25rem', sm: '4.5rem', md: '6rem', lg: '7rem' },
          fontWeight: 700, color: DS.text, letterSpacing: '-0.04em', lineHeight: 1.0, mb: 0.5,
        }}>
          Your data.
        </Typography>
        <Typography sx={{
          fontSize: { xs: '3.25rem', sm: '4.5rem', md: '6rem', lg: '7rem' },
          fontWeight: 700, color: DS.textMuted, letterSpacing: '-0.04em', lineHeight: 1.0, mb: 5,
        }}>
          Instantly understood.
        </Typography>

        {/* Step flow */}
        <Typography sx={{
          display: { xs: 'none', md: 'block' },
          fontSize: { xs: '1rem', md: '1.1875rem' }, color: DS.textFaint,
          fontWeight: 400, letterSpacing: '-0.01em', mb: 6,
        }}>
          {['Upload', 'Clean', 'Join', 'Filter', 'Visualize', 'Naturally question it'].map((step, i, arr) => (
            <React.Fragment key={step}>
              <Box component="span" sx={{ color: DS.textMuted }}>{step}</Box>
              {i < arr.length - 1 && (
                <Box component="span" sx={{ mx: { xs: 1, md: 1.5 }, color: DS.textFaint, fontWeight: 300 }}>→</Box>
              )}
            </React.Fragment>
          ))}
        </Typography>

        {/* CTAs */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mb: 2.5 }}>
          <Button variant="contained" size="large" onClick={onGuestAccess} sx={{
            fontSize: '1rem', fontWeight: 600,
            px: 4, py: 1.375, borderRadius: '9999px',
            boxShadow: '0 4px 24px rgba(107,94,248,0.35)',
            '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 32px rgba(107,94,248,0.45)' },
            transition: 'all 0.15s ease',
          }}>
            Get Started — It's Free
          </Button>
          <Button variant="outlined" size="large" onClick={onStart} sx={{
            fontSize: '1rem', fontWeight: 500,
            px: 4, py: 1.375, borderRadius: '9999px',
          }}>
            Sign In
          </Button>
        </Box>

        <Typography sx={{ fontSize: '0.8125rem', color: DS.textFaint }}>
          No credit card required
        </Typography>

        {/* ── Feature strip card ── */}
        <Box sx={{
          mt: { xs: 8, md: 12 }, width: '100%', maxWidth: 860,
          backgroundColor: DS.bg2,
          border: `1px solid ${DS.border}`,
          borderRadius: '16px',
          px: { xs: 3, md: 6 }, py: { xs: 5, md: 6 },
          display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
          gap: { xs: 4, sm: 0 },
        }}>
          {[
            { icon: <CloudUploadIcon sx={{ fontSize: 24, color: DS.accent }} />, iconBg: DS.accentDim, label: 'Upload', sub: 'Any dataset, any size' },
            { icon: <BoltIcon sx={{ fontSize: 24, color: DS.amber }} />, iconBg: DS.amberDim, label: 'Analyze', sub: 'Instant intelligence' },
            { icon: <BarChartIcon sx={{ fontSize: 24, color: '#10B981' }} />, iconBg: 'rgba(16,185,129,0.12)', label: 'Visualize', sub: 'Beautiful insights' },
          ].map((item, i) => (
            <Box key={item.label} sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
              px: { sm: 3 },
              borderRight: { xs: 'none', sm: i < 2 ? `1px solid ${DS.border}` : 'none' },
              borderBottom: { xs: i < 2 ? `1px solid ${DS.border}` : 'none', sm: 'none' },
              pb: { xs: i < 2 ? 3 : 0, sm: 0 },
            }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: '14px', backgroundColor: item.iconBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
              }}>
                {item.icon}
              </Box>
              <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: DS.text, mb: 0.5 }}>{item.label}</Typography>
              <Typography sx={{ fontSize: '0.875rem', color: DS.textMuted }}>{item.sub}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── About / Stats section ── */}
      <Box id="about-section" sx={{ backgroundColor: DS.bg2, borderTop: `1px solid ${DS.border}`, borderBottom: `1px solid ${DS.border}`, px: { xs: 3, md: 6 }, py: { xs: 8, md: 10 } }}>
        <Box sx={{ maxWidth: 680, mx: 'auto', textAlign: 'center', mb: { xs: 7, md: 9 } }}>
          <Typography sx={{
            fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 700, color: DS.text,
            letterSpacing: '-0.03em', mb: 2.5, lineHeight: 1.2,
          }}>
            Built for curious minds, not data scientists.
          </Typography>
          <Typography sx={{ fontSize: '1.0625rem', color: DS.textMuted, lineHeight: 1.75 }}>
            DataScope is made for anyone who wants to quickly explore datasets and extract meaningful
            insights — without complex tools, formulas, or a line of code. Upload your data, see instant
            visualizations, and ask questions in plain English.
          </Typography>
        </Box>

        {/* Stats */}
        <Box sx={{
          maxWidth: 860, mx: 'auto',
          display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
          gap: { xs: 6, sm: 4 }, textAlign: 'center',
        }}>
          {[
            { value: '1M+',       color: DS.accent,   label: 'Rows supported' },
            { value: '100%',      color: DS.amber,     label: 'No-code solution' },
            { value: 'Real-time', color: '#10B981',    label: 'Instant results' },
          ].map(stat => (
            <Box key={stat.label}>
              <Typography sx={{
                fontSize: { xs: '3rem', md: '3.75rem' }, fontWeight: 700, color: stat.color,
                letterSpacing: '-0.03em', lineHeight: 1.1, mb: 0.75,
              }}>
                {stat.value}
              </Typography>
              <Typography sx={{ fontSize: '0.9375rem', color: DS.textMuted }}>{stat.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Bottom CTA ── */}
      <Box sx={{ backgroundColor: DS.bg, px: { xs: 3, md: 6 }, py: { xs: 8, md: 10 }, textAlign: 'center' }}>
        <Typography sx={{
          fontSize: { xs: '1.75rem', md: '2.75rem' }, fontWeight: 700, color: DS.text,
          letterSpacing: '-0.03em', mb: 2,
        }}>
          Ready to understand your data?
        </Typography>
        <Typography sx={{ fontSize: '1.0625rem', color: DS.textMuted, mb: 5, maxWidth: 480, mx: 'auto' }}>
          Get insights in minutes. No setup, no code, no data science degree required.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" size="large" onClick={onGuestAccess} sx={{
            fontSize: '1rem', fontWeight: 600,
            px: 4, py: 1.375, borderRadius: '9999px',
            boxShadow: '0 4px 24px rgba(107,94,248,0.35)',
            '&:hover': { transform: 'translateY(-1px)' }, transition: 'all 0.15s ease',
          }}>
            Get Started — It's Free
          </Button>
          <Button variant="outlined" size="large" onClick={onStart} sx={{
            fontSize: '1rem', fontWeight: 500, px: 4, py: 1.375, borderRadius: '9999px',
          }}>
            Sign In
          </Button>
        </Box>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{
        backgroundColor: DS.bg2, borderTop: `1px solid ${DS.border}`,
        px: { xs: 3, md: 6 }, py: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 22, height: 22, backgroundColor: DS.accent, borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: '#fff', fontSize: '9px', fontWeight: 700 }}>DS</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.8125rem', color: DS.textFaint }}>DataScope © 2025</Typography>
        </Box>
        <Typography sx={{ fontSize: '0.8125rem', color: DS.textFaint }}>Built for people, not engineers.</Typography>
      </Box>

    </Box>
  );
};

export default LandingPage;
